<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import { session, applySession, statusLabel, isExpiringSoon } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { TG_SUB_LINK } from '../lib/bot-links';
  import { api, ApiError } from '../lib/api';
  import { getInitData, isInsideTelegram, openBotLink } from '../lib/telegram';
  import { formatDate } from '../lib/format';
  import type { ProgressByCategoryItemDTO, SubscriptionStatus } from '../lib/types';

  let progress = $state<ProgressByCategoryItemDTO[] | null>(null);
  // Доступний реферальний баланс для блока-входу. null — ще вантажимо або не
  // вдалось: блок усе одно показуємо, бо це вхід у розділ, а не звіт про гроші.
  let referralAvailable = $state<number | null>(null);

  // Per-product status pill (mini-courses in «Мої продукти»). Mirrors the
  // club status colours used in the info card above.
  const PRODUCT_STATUS: Record<SubscriptionStatus, { text: string; cls: string }> = {
    active: { text: 'Активний', cls: 'green' },
    inactive: { text: 'Закінчився', cls: 'red' },
    none: { text: 'Немає доступу', cls: 'muted' },
  };

  onMount(async () => {
    try {
      if (isInsideTelegram()) {
        const dto = await api.authMe(getInitData());
        applySession(dto);
      } else {
        const dto = await api.authSession();
        applySession(dto);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        replace(import.meta.env.DEV ? '/' : '/login');
        return;
      }
    }

    try {
      const { items } = await api.myProgressByCategory();
      progress = items;
    } catch {
      progress = [];
    }

    // Коли рефералка прихована, запит не робимо: він ще й створив би людині код.
    if ($session?.referralsVisible) {
      try {
        referralAvailable = (await api.referralMe()).available;
      } catch {
        referralAvailable = null;
      }
    }
  });

  const initials = $derived(
    $session
      ? (($session.name?.charAt(0) ?? '') + ($session.lastName?.charAt(0) ?? '')).toUpperCase() ||
          '?'
      : '?',
  );

  const statusMeta = $derived($session ? statusLabel($session.status) : null);
</script>

{#if $session}
  <div class="screen">
    <TopBar title="Профіль" />
    <div class="scroll card-list">
      <div class="profile-head">
        <div class="profile-avatar">
          {#if $session.photo}
            <img src={$session.photo} alt="" />
          {:else}
            {initials}
          {/if}
        </div>
        <div class="profile-name">{$session.fullName}</div>
        {#if $session.username}
          <div class="profile-username">@{$session.username}</div>
        {/if}
      </div>

      <div class="info-card">
        <div class="info-row">
          <div class="info-label">Статус</div>
          <div class="info-value {statusMeta?.color ?? 'muted'}">
            {statusMeta?.text ?? '—'}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Тариф</div>
          <div class="info-value">{$session.tariff ?? '—'}</div>
        </div>
        <div class="info-row">
          <div class="info-label">Початок</div>
          <div class="info-value">{formatDate($session.subscriptionStartDate)}</div>
        </div>
        <div class="info-row">
          <div class="info-label">Закінчення</div>
          <div
            class="info-value"
            class:red={$session.status === 'inactive'}
            class:yellow={$session.status === 'active' && isExpiringSoon($session.subscriptionEndDate)}
            class:muted={$session.status === 'none'}
          >
            {formatDate($session.subscriptionEndDate)}
          </div>
        </div>
      </div>

      {#if $session.products.length > 0}
        <div class="section-title">Мої продукти</div>
        <div class="products-list">
          {#each $session.products as p (p.id)}
            <div class="product-item">
              <div class="product-info">
                <div class="product-name">{p.title}</div>
                <div
                  class="product-meta"
                  class:red={p.status === 'inactive'}
                  class:yellow={p.status === 'active' && isExpiringSoon(p.endDate)}
                >
                  {p.endDate
                    ? `до ${formatDate(p.endDate)}`
                    : p.status === 'active'
                      ? 'Доступ без обмежень'
                      : '—'}
                </div>
              </div>
              <span class="prod-badge {PRODUCT_STATUS[p.status].cls}">
                {PRODUCT_STATUS[p.status].text}
              </span>
            </div>
          {/each}
        </div>
      {/if}

      {#if $session.referralsVisible}
        <a class="referral-card" href="#/referral">
          <div class="referral-icon"><Icon name="gift" size="lg" /></div>
          <div class="referral-body">
            <div class="referral-title">Реферальна система</div>
            <div class="referral-sub">
              {#if referralAvailable === null}
                Запрошуй подруг і отримуй бонуси
              {:else}
                Доступно бонусів: <strong>{referralAvailable.toFixed(2)} ₴</strong>
              {/if}
            </div>
          </div>
          <div class="referral-chevron" aria-hidden="true">›</div>
        </a>
      {/if}

      {#if $session.isAdmin}
        <a
          class="cta cta-admin"
          href="#/admin/dashboard"
          onclick={() => adminMode.set('admin')}
        >
          <span class="cta-admin-icon"><Icon name="shield-check" /></span> Перейти в адмін панель
        </a>
      {/if}

      <a
        class="cta cta-sub"
        href={TG_SUB_LINK}
        target="_blank"
        rel="noopener"
        onclick={(e) => openBotLink(e, TG_SUB_LINK)}
      >
        {$session.status === 'inactive'
          ? 'Поновити підписку'
          : $session.status === 'none'
            ? 'Оформити підписку'
            : 'Продовжити підписку'}
      </a>

      <div class="section-title">Мій прогрес</div>
      {#if progress === null}
        <div class="placeholder">Завантажуємо…</div>
      {:else if progress.length > 0}
        <div class="progress-list">
          {#each progress as p (p.id)}
            <a class="progress-item" href={`#/section/${p.id}`}>
              <div class="progress-hd">
                <div class="progress-name"><Icon name={p.icon} size="xs" /> {p.name}</div>
                <div class="progress-pct">{p.completed}/{p.total}</div>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" style="width: {p.pct}%"></div>
              </div>
            </a>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
  }
  .scroll::-webkit-scrollbar {
    width: 3px;
  }
  .scroll::-webkit-scrollbar-thumb {
    background: var(--border2);
    border-radius: 2px;
  }

  .profile-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 22px;
  }
  .profile-avatar {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-light);
    font-size: 30px;
    font-weight: 600;
    margin-bottom: 12px;
    overflow: hidden;
    border: 3px solid var(--pu-light);
  }
  .profile-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .profile-name {
    font-size: 19px;
    font-weight: 600;
    color: var(--text);
  }
  .profile-username {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 4px;
  }

  .info-card {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  .info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 16px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
  }
  .info-row:last-child {
    border-bottom: none;
  }
  .info-label {
    color: var(--text-muted);
    font-weight: 500;
  }
  .info-value {
    color: var(--text);
    font-weight: 600;
  }
  .info-value.green {
    color: var(--status-active);
  }
  .info-value.yellow {
    color: var(--status-expiring);
  }
  .info-value.red {
    color: var(--status-inactive);
  }
  .info-value.muted {
    color: var(--text-muted);
  }

  .cta {
    display: block;
    text-align: center;
    background: var(--pu);
    color: var(--text-light);
    padding: 14px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    margin-bottom: 16px;
  }
  /* Subscription CTA — same dark gradient regardless of status (active /
     expiring / inactive / none) so the entry point looks consistent. Status
     differentiation lives in the button label, not the visual style. */
  .cta-sub {
    background: var(--pu);
    color: var(--text-light);
  }
  /* Admin shortcut — same dark gradient family as the subscription CTA,
     differentiated by the shield icon and label. Stacked above the sub CTA. */
  .cta-admin {
    background: var(--pu);
    color: var(--text-light);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .cta-admin-icon {
    font-size: 15px;
  }

  .section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 10px;
  }

  /* Вхід у реферальний розділ. Це рядок-навігація, а не CTA: візуально ближчий
     до карток «Мої продукти», щоб не конкурувати з кнопкою підписки. */
  .referral-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 14px;
    margin-bottom: 16px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    transition: border-color 0.2s var(--ease), transform 0.15s var(--ease);
  }
  .referral-card:active {
    transform: scale(0.985);
    border-color: var(--border2);
  }
  .referral-icon {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: var(--pu-light);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
  }
  .referral-body {
    flex: 1;
    min-width: 0;
  }
  .referral-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .referral-sub {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 3px;
  }
  .referral-sub strong {
    color: var(--accent-text);
    font-weight: 600;
  }
  .referral-chevron {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 1;
    color: var(--text-muted);
  }

  /* «Мої продукти» — one card per owned mini-course with its own status +
     end date, so multiple products read clearly side by side. */
  .products-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }
  .product-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
  }
  .product-info {
    min-width: 0;
    flex: 1;
  }
  .product-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .product-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 3px;
  }
  .product-meta.red { color: var(--status-inactive); }
  .product-meta.yellow { color: var(--status-expiring); }
  .prod-badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 11px;
    border-radius: 999px;
    background: var(--bg);
    color: var(--text-muted);
  }
  .prod-badge.green {
    background: var(--green-light, var(--green-light));
    color: var(--status-active);
  }
  .prod-badge.red {
    background: var(--danger-bg);
    color: var(--status-inactive);
  }
  .progress-item {
    display: block;
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 11px 13px;
    margin-bottom: 8px;
    background: var(--bg2);
    color: inherit;
    text-decoration: none;
    transition: border-color 0.2s var(--ease), transform 0.15s var(--ease);
  }
  .progress-item:active {
    transform: scale(0.985);
    border-color: var(--border2);
  }
  .progress-hd {
    display: flex;
    justify-content: space-between;
    margin-bottom: 7px;
  }
  .progress-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }
  .progress-pct {
    font-size: 11px;
    color: var(--accent-text);
    font-weight: 600;
  }
  .progress-bar {
    height: 4px;
    background: var(--pu-light);
    border-radius: 2px;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    border-radius: 2px;
    background: var(--pu2);
    transition: width 0.3s var(--ease);
  }
  .placeholder {
    padding: 12px;
    text-align: center;
    border: 1px dashed var(--border2);
    border-radius: 10px;
    background: var(--bg2);
    font-size: 12px;
    color: var(--text-muted);
  }
</style>
